/**
 * Purchasing Settings Tab - client-managed purchasing reference data
 * (reason codes, contract types, commodity groups/types, etc.).
 * CR-5888: commodity management was relocated here from Platform Admin so
 * client admins can maintain it alongside the other PO reference lists.
 */

import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { canViewEmailTemplates } from './emailTemplates/useEmailTemplates';
import { ReasonCodesSection } from './ReasonCodesSection';
import { PurchasingEmailNotificationsSection } from './PurchasingEmailNotificationsSection';
import { OpenOrdersSection } from './OpenOrdersSection';
import { SupplierDeclineSetting } from './SupplierDeclineSetting';
import { AutoApproveDeliveryAppealSection } from './AutoApproveDeliveryAppealSection';
import { AutoRaiseDeliveryAppealSection } from './AutoRaiseDeliveryAppealSection';
import { OtifSundayRuleSection } from './OtifSundayRuleSection';
import { ProdumaxExportSection } from './ProdumaxExportSection';
import { PriceBookTypesAndSharingSection } from './PriceBookTypesAndSharingSection';
import { ContractExpiryAlertSection } from './ContractExpiryAlertSection';
import { CommodityGroupsSection } from './CommodityGroupsSection';
import { RFQTypesSection } from './RFQTypesSection';

export function PurchasingTab() {
  // CR-6723: the Produmax ASN Export settings are only relevant when the
  // Call-Off module is enabled (per-supplier call_off_enabled relationships,
  // surfaced as the switch_call_off_tab feature flag). Hide the section when
  // the module is off; the stored asn_produmax_export flag is untouched so
  // re-enabling Call-Off restores the section without reconfiguration.
  const { user } = useAuth();
  const { isFeatureEnabled, purchasingTabs } = useMenuSettings();
  const showProdumaxExport = isFeatureEnabled('switch_call_off_tab');
  // CR-6164: RFQ Types follow the RFQ module switch (the platform's
  // purchasing_tabs.rfq, the same flag that gates /rfq) and the backend's
  // IsHelixOrCustomerAdmin write gate. Hide the list rather than offer
  // controls that would fail — e.g. to a logistics admin, whom Admin Setup
  // admits but the API refuses. Same customer-admin-or-Helix rule AdminSetup
  // applies to its customerAdminOnly sections.
  const showRfqTypes = purchasingTabs.rfq !== false && canViewEmailTemplates(user);

  return (
    <div className="space-y-6">
      {/* Email Notifications (CR-4421) */}
      <PurchasingEmailNotificationsSection />

      {/* CR-5892: Allow Supplier Decline — relocated from Platform Admin
          (Helix-only) so client admins control supplier order-decline for
          their own tenant. Stored per-company; the supplier portal enforces it. */}
      <SupplierDeclineSetting />

      {/* Open Orders visibility (CR-4422) */}
      <OpenOrdersSection />

      {/* Auto Approve Delivery Appeal (CR-4423) */}
      <AutoApproveDeliveryAppealSection />

      {/* Auto Raise Delivery Appeals (CR-7068) */}
      <AutoRaiseDeliveryAppealSection />

      {/* OTIF day counting: calendar vs non-Sunday walk (METRIC-DEFINITIONS Q2) */}
      <OtifSundayRuleSection />

      {/* Produmax ASN Export feature flag (PNA) — CR-6723: only while the
          Call-Off module is enabled for this tenant */}
      {showProdumaxExport && <ProdumaxExportSection />}

      {/* Reason Codes */}
      <ReasonCodesSection />

      {/* CR-7321: Price Book Types & Sharing — the configurable types (which
          drive contract auto-numbering, CR-1441) and the sharing controls
          (CR-6346) merged into one section, since the types you configure ARE
          the price book types the sharing controls share. */}
      <PriceBookTypesAndSharingSection />

      {/* CR-7231: Contract Expiry Alert — next to Price Book Sharing, as the
          ticket asked; same "Days Prior" shape as Quality's Approval Expiry
          Alert. */}
      <ContractExpiryAlertSection />

      {/* CR-5888: Manage Commodity Groups and Types — relocated from Platform
          Admin so client admins can maintain them next to the other PO
          reference lists. Backend restricts writes to client admins. */}
      <CommodityGroupsSection />

      {/* CR-6164: RFQ Types — the Create RFQ wizard's dropdown. Relocated
          here from Platform Admin (which keeps its own copy for Helix) so
          customer admins manage their own list while the RFQ module is on. */}
      {showRfqTypes && <RFQTypesSection />}
    </div>
  );
}
